import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Heading,
  Image,
  InputGroup,
  InputLeftElement,
  Input,
  useDisclosure,
  IconButton,
  Tabs,
  TabList,
  TabIndicator,
  Tab,
  TabPanel,
  TabPanels,
  Divider,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';

import PlusIcon from '../../../assets/icons/plus.svg';
import DocIcon from '../../../assets/icons/document.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filters.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import {
  useAddUserFilterPrefMutation,
  useGetAllMyTasksListQuery,
  useGetAllTeamTasksListQuery,
  useGetUserFilterPrefQuery,
  useGetUserPreferenceQuery,
} from '../../../services';
import { CenteredSpinner, Paginator } from '../../../components';
import {
  Sortable,
  Task,
  Response,
  TaskStatus,
  TaskFilters,
  TaskDynamicColumn,
  FileStatus,
} from '../../../models';
import {
  ExportTasks,
  NewTaskMetaDataModal,
  SavedTask,
  TaskDetail,
} from '../components';
import { tasksTabs } from '../utils';
import { isToday } from 'date-fns';
import { ReactComponent as MyTasksIcon } from '../../../assets/tasks/my-tasks.svg';
import { ReactComponent as MyTasksSelectedIcon } from '../../../assets/tasks/my-tasks-selected.svg';
import { ReactComponent as TeamTasksIcon } from '../../../assets/tasks/team-tasks.svg';
import { ReactComponent as TeamTasksSelectedIcon } from '../../../assets/tasks/team-tasks-selected.svg';
import { Can } from '../../../providers';
import { TaskFiltersDrawer } from '../components/TaskFiltersDrawer';
import { useTypedSelector } from '../../../hooks';
import Pusher from 'pusher-js';
import { NewTaskUploadFilesInBackground } from '../components/NewTaskUploadFileInBackground';
import { NewTaskUsingExcel } from '../components/NewTaskUsingExcel';
import { TaskDynamicColumnsDrawer } from '../components/TaskDynamicColumnsDrawer';
import { TaskDetailHeader } from '../components/TaskDetailHeader';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const Tasks: FC<{
  isRenderedAtHome?: boolean;
  isNewTaskFromHome?: number;
  phaseId?: string;
  selectedProjectId?: number;
}> = ({ isRenderedAtHome, isNewTaskFromHome, phaseId, selectedProjectId }) => {
  const [newTaskFiles, setNewTaskFiles] = useState<
    { file: File; status: FileStatus }[]
  >([]);
  const [newTaskId, setNewTaskId] = useState<number | undefined>(undefined);

  const [selectedWorkflowId, setSelectedWorkflowId] = useState<number>();
  const [newTaskType, setNewTaskType] = useState<'import_excel' | 'manual'>(
    'manual'
  );
  const [parentTaskId, setparentTaskId] = useState<number | undefined>();
  const [selectedTaskId, setSelectedTaskId] = useState<number>();
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
  const [isDynamicColumnsOpen, setIsDynamicColumnsOpen] =
    useState<boolean>(false);
  const [userDynamicColumns, setUserDynamicColumns] = useState<
    TaskDynamicColumn[]
  >([]);
  const [filters, setFilters] = useState<TaskFilters>();
  const [searchTerm, setSearchTerm] = useState<string>();
  const [page, setPage] = useState(0);
  const [tasksStatus, setTasksStatus] = useState<TaskStatus | undefined>(
    TaskStatus.InProgress
  );

  const { data: userPreferenceData, status: userPreferenceStatus } =
    useGetUserPreferenceQuery();

  const { userDetails } = useTypedSelector(state => state.auth);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { tabName } = useParams();
  const navigate = useNavigate();

  const {
    isOpen: isNewTaskMetaDataModalOpen,
    onOpen: onOpenNewTaskMetaDataModal,
    onClose: onCloseNewTaskMetaDataModal,
  } = useDisclosure();

  const {
    isOpen: isSavedTaskModalOpen,
    onOpen: onOpenSavedTaskModal,
    onClose: onCloseSavedTaskModal,
  } = useDisclosure();

  const {
    isOpen: isImportExcelOpen,
    onOpen: onOpenImportExcelModal,
    onClose: onCloseImportExcelModal,
  } = useDisclosure();

  const isTeamTasks = location.pathname.includes('my-team');

  const [addUserFilterPref, { isLoading: isAddingUserFilterPrefLoading }] =
    useAddUserFilterPrefMutation();

  const saveFilters = async (receivedFilters: TaskFilters) => {
    const response = await addUserFilterPref({
      filterPreferenceId: null,
      filterPreference: JSON.stringify(receivedFilters),
    });

    // JUDGMENT CALL (recorded in the ticket report): this preference write
    // stays SILENT on success exactly as before — no new toast on the happy
    // path — but a rejected save is no longer invisible. The filters remain
    // applied to the current view either way; only their persistence failed,
    // and the copy says exactly that.
    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not save your filters',
        description:
          'They are applied to this view but were not saved for next time.',
      });
    }
  };

  const {
    data: myTasks,
    isLoading: isMyTasksLoading,
    isFetching: isMyTasksFetching,
  } = useGetAllMyTasksListQuery(
    {
      page,
      name: debouncedSearchTerm,
      status: tasksStatus,
      isLate: tabName === 'late' || undefined,
      phaseId,
      projectId: selectedProjectId,
      ...filters,
    },
    {
      skip: isTeamTasks,
    }
  );

  const { data: userFiltersData } = useGetUserFilterPrefQuery(undefined);

  const {
    data: teamTasks,
    isLoading: isTeamTasksLoading,
    isFetching: isTeamTasksFetching,
  } = useGetAllTeamTasksListQuery(
    {
      page,
      name: debouncedSearchTerm,
      status: tasksStatus,
      isLate: tabName === 'late' || undefined,
      phaseId,
      projectId: selectedProjectId,
      ...filters,
    },
    {
      skip: !isTeamTasks,
    }
  );

  const startNewLinkedTask = (parentTaskId: number) => {
    onCloseSavedTaskModal();
    setparentTaskId(parentTaskId);
    setSelectedTaskId(undefined);
    setNewTaskType('manual');
    onOpenNewTaskMetaDataModal();
  };

  let index = tasksTabs.findIndex(({ link }) => link === tabName);
  index = index === -1 ? 0 : index;

  let tasks: Response<Sortable<Task>>;
  const [newTasks, setNewTasks] = useState<Task[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]);

  useEffect(() => {
    if (tabName === 'current' || !tabName)
      setTasksStatus(TaskStatus.InProgress);
    else if (tabName === 'completed') setTasksStatus(TaskStatus.Completed);
    else setTasksStatus(undefined);
  }, [tabName]);

  useEffect(() => {
    if (userDetails?.id) {
      // Initialize Pusher
      const pusher = new Pusher('ad53f880c217d05a03db', {
        cluster: 'eu',
      });

      // Create dynamic channel name
      const channelName = `export_${userDetails?.id}`;
      const channel = pusher.subscribe(channelName);

      // Bind to the event
      channel.bind('export_tasks_excel', (data: any) => {
        if (data && data?.exportOutput) {
          window.open(data?.exportOutput, '_blank');
        }
      });

      // Clean up on component unmount
      return () => {
        pusher.unsubscribe(channelName);
      };
    }
  }, [userDetails?.id]);

  useEffect(() => {
    if (myTasks?.data) {
      tasks = myTasks;
    }

    if (teamTasks?.data) {
      tasks = teamTasks;
    }

    if (tasks?.data) {
      setNewTasks(
        tasks.data.content.filter(task => isToday(new Date(task.modifiedAt)))
      );

      setAllTasks(
        tasks.data.content.filter(task => !isToday(new Date(task.modifiedAt)))
      );
    }
  }, [myTasks?.data, teamTasks?.data]);

  useEffect(() => {
    if (isNewTaskFromHome) onOpenNewTaskMetaDataModal();
  }, [isNewTaskFromHome]);

  useEffect(() => {
    if (selectedTaskId) onOpenSavedTaskModal();
  }, [selectedTaskId]);

  return (
    <>
      <TaskFiltersDrawer
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        filters={filters}
        setFilters={setFilters}
        saveFilters={saveFilters}
      />
      {/*
        These two drawers take DIFFERENT prop names, and the columns drawer was
        being handed the filters drawer's (`filters` / `setFilters`) — so it
        received undefined for both and ignored the configured columns. Pass
        each drawer the props it declares.
      */}
      <TaskDynamicColumnsDrawer
        isOpen={isDynamicColumnsOpen}
        onClose={() => setIsDynamicColumnsOpen(false)}
        userDynamicColumns={userDynamicColumns}
        setUserDynamicColumns={setUserDynamicColumns}
      />
      <Box>
        {isNewTaskMetaDataModalOpen && (
          <NewTaskMetaDataModal
            isOpen={isNewTaskMetaDataModalOpen}
            onClose={workflowId => {
              setSelectedWorkflowId(workflowId);
              onCloseNewTaskMetaDataModal();
              if (newTaskType === 'import_excel') {
                onOpenImportExcelModal();
              } else {
                onOpenSavedTaskModal();
              }
            }}
          />
        )}

        {isImportExcelOpen && selectedWorkflowId && (
          <NewTaskUsingExcel
            workflowId={Number(selectedWorkflowId)}
            isOpen={isImportExcelOpen}
            onClose={() => {
              onCloseImportExcelModal();
              setSelectedTaskId(undefined);
            }}
          />
        )}

        {(selectedTaskId || (isSavedTaskModalOpen && selectedWorkflowId)) && (
          <SavedTask
            workflowId={Number(selectedWorkflowId)}
            isOpen={isSavedTaskModalOpen}
            onClose={() => {
              onCloseSavedTaskModal();
              setSelectedTaskId(undefined);
            }}
            taskId={selectedTaskId}
            setNewTaskId={setNewTaskId}
            newTaskFiles={newTaskFiles}
            setNewTaskFiles={setNewTaskFiles}
            startNewLinkedTask={startNewLinkedTask}
            // The sub-task link. `SavedTask` declares this prop under the
            // backend's name (`parentTaskId`); it used to declare
            // `linkedTaskId`, so this value was dropped on the way in.
            parentTaskId={parentTaskId}
            openTask={(taskId: number) => {
              onCloseSavedTaskModal();
              setTimeout(() => {
                setSelectedTaskId(taskId);
              }, 500);
            }}
          />
        )}

        <VStack align="stretch">
          <HStack justify="space-between" my="4" align="start">
            <Heading color="custom_blue.1" fontSize="2xl">
              {isTeamTasks ? 'My Team' : 'My Tasks'}
            </Heading>

            <HStack>
              <Button
                onClick={() => {
                  setparentTaskId(undefined);
                  setSelectedTaskId(undefined);
                  setNewTaskType('import_excel');
                  onOpenNewTaskMetaDataModal();
                }}
                sx={{ border: '1px solid #12B76A ' }}
                bg="#F6FEF9"
                _hover={{ bg: '#F6FEF9', color: '#12B76A' }}
                color="#12B76A"
                fontWeight="semibold"
                fontSize="sm"
                marginRight="2"
              >
                <Image src={DocIcon} alt="doc icon" boxSize="5" mr="1" />
                Import Excel Sheet
              </Button>
              {/*  */}
              <Can I="POST" a="company/taskBuilding/task">
                <Button
                  onClick={() => {
                    setparentTaskId(undefined);
                    setSelectedTaskId(undefined);
                    setNewTaskType('manual');
                    onOpenNewTaskMetaDataModal();
                  }}
                  bg="custom_orange.2"
                  _hover={{ bg: 'custom_orange.2' }}
                  color="white"
                  fontWeight="semibold"
                  fontSize="sm"
                  marginRight="6"
                >
                  <Image src={PlusIcon} alt="plus icon" boxSize="5" mr="1" />
                  Start New Task
                </Button>
              </Can>
              {!isRenderedAtHome && (
                <Can I="GET" a="company/taskBuilding/teamTaskList">
                  <HStack>
                    <IconButton
                      bg={!isTeamTasks ? 'custom_red.3' : 'white'}
                      _hover={{ bg: !isTeamTasks ? 'custom_red.3' : 'white' }}
                      icon={
                        !isTeamTasks ? <MyTasksSelectedIcon /> : <MyTasksIcon />
                      }
                      onClick={() => {
                        navigate('/tasks');
                      }}
                      aria-label={''}
                    />
                    <IconButton
                      bg={isTeamTasks ? 'custom_red.3' : 'white'}
                      _hover={{ bg: !isTeamTasks ? 'custom_red.3' : 'white' }}
                      icon={
                        !isTeamTasks ? (
                          <TeamTasksIcon />
                        ) : (
                          <TeamTasksSelectedIcon />
                        )
                      }
                      onClick={() => navigate('/tasks/my-team')}
                      aria-label={''}
                    />
                  </HStack>
                </Can>
              )}
            </HStack>
          </HStack>

          <Tabs
            position="relative"
            variant="unstyled"
            colorScheme="orange"
            index={index}
            onChange={tabIndex => {
              const { link } = tasksTabs[tabIndex];
              const isAtHome = !location.pathname.startsWith('/tasks');
              navigate(
                `${isAtHome ? '' : '/tasks'}/${
                  isTeamTasks ? 'my-team' : 'my-tasks'
                }/${link}`
              );
            }}
          >
            <VStack
              bg="white"
              borderRadius="md"
              px="4"
              pt="2"
              border="1px"
              borderColor="custom_gray.3"
              minH="80vh"
              align="stretch"
            >
              <Box px="2">
                <TabList>
                  {tasksTabs.map(({ name }) => (
                    <Tab
                      key={name}
                      fontSize="sm"
                      fontWeight="semibold"
                      color="custom_gray.1"
                      _selected={{ color: 'custom_orange.2' }}
                    >
                      {name}
                    </Tab>
                  ))}
                </TabList>
                <TabIndicator
                  mt="-1.5px"
                  height="2px"
                  bg="custom_orange.2"
                  borderRadius="1px"
                />

                <Divider />
              </Box>

              <TabPanels>
                {tasksTabs.map(({ name }) => (
                  <TabPanel px="0" key={name}>
                    <VStack align="stretch" justify="space-between" minH="70vh">
                      <VStack align="stretch" flex="1">
                        {!isRenderedAtHome && (
                          <HStack justify="space-between">
                            <HStack flex="1">
                              <InputGroup flexBasis="45%">
                                <InputLeftElement pointerEvents="none">
                                  <SearchIcon color="gray.300" />
                                </InputLeftElement>
                                <Input
                                  placeholder="Search"
                                  onChange={e => setSearchTerm(e.target.value)}
                                />
                              </InputGroup>

                              <Button
                                leftIcon={<FiltersIcon />}
                                color="custom_blue.1"
                                fontWeight="semibold"
                                fontSize="sm"
                                boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
                                border="1px"
                                borderColor="custom_blue.3"
                                bg="none"
                                _hover={{ bg: 'none' }}
                                onClick={() => setIsFiltersOpen(true)}
                              >
                                Filters
                              </Button>
                            </HStack>

                            <ExportTasks
                              filters={{
                                page: 0,
                                size: 1000000,
                                name: debouncedSearchTerm,
                                status: tasksStatus,
                                isLate: tabName === 'late' || undefined,
                                phaseId,
                                projectId: selectedProjectId,
                                ...filters,
                              }}
                            />
                            <Button
                              leftIcon={<FiltersIcon />}
                              color="custom_blue.1"
                              fontWeight="semibold"
                              fontSize="sm"
                              boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
                              border="1px"
                              borderColor="custom_blue.3"
                              bg="none"
                              _hover={{ bg: 'none' }}
                              onClick={() => setIsDynamicColumnsOpen(true)}
                            >
                              Edit Task Columns
                            </Button>
                          </HStack>
                        )}

                        {isMyTasksLoading ||
                        isMyTasksFetching ||
                        isTeamTasksLoading ||
                        isTeamTasksFetching ? (
                          <CenteredSpinner />
                        ) : (
                          <>
                            <Box my="4">
                              {newTasks.length ? (
                                <Box>
                                  <Box
                                    fontSize="lg"
                                    fontWeight="semibold"
                                    color="custom_gray.9"
                                    my="2"
                                  >
                                    New
                                  </Box>
                                  <VStack align="start">
                                    <TaskDetailHeader
                                      userPreferenceData={
                                        userPreferenceData?.data
                                      }
                                    />
                                    {newTasks.map(task => (
                                      <Box
                                        key={task.id}
                                        onClick={() =>
                                          setSelectedTaskId(task.id)
                                        }
                                        cursor="pointer"
                                        w="full"
                                      >
                                        <TaskDetail
                                          userPreferenceData={
                                            userPreferenceData?.data
                                          }
                                          task={task}
                                        />
                                      </Box>
                                    ))}
                                  </VStack>
                                </Box>
                              ) : null}

                              {allTasks.length ? (
                                <Box>
                                  <Box
                                    fontSize="lg"
                                    fontWeight="semibold"
                                    color="custom_gray.9"
                                    my="2"
                                  >
                                    All
                                  </Box>

                                  <VStack
                                    align="start"
                                    className="scroll-container"
                                    sx={{
                                      width: '100%',
                                      overflowX: 'scroll',
                                    }}
                                  >
                                    <TaskDetailHeader
                                      userPreferenceData={
                                        userPreferenceData?.data
                                      }
                                    />
                                    {allTasks.map(task => (
                                      <Box
                                        key={task.id}
                                        onClick={() =>
                                          setSelectedTaskId(task.id)
                                        }
                                        cursor="pointer"
                                        w="auto"
                                      >
                                        <TaskDetail
                                          userPreferenceData={
                                            userPreferenceData?.data
                                          }
                                          task={task}
                                        />
                                      </Box>
                                    ))}
                                  </VStack>
                                </Box>
                              ) : null}
                            </Box>
                          </>
                        )}
                      </VStack>

                      <Paginator
                        pageCount={
                          myTasks?.data.totalPages ||
                          teamTasks?.data.totalPages ||
                          0
                        }
                        onPageChange={page => setPage(page)}
                      />
                    </VStack>
                  </TabPanel>
                ))}
              </TabPanels>
            </VStack>
          </Tabs>
        </VStack>

        {newTaskId && newTaskFiles.length > 0 && (
          <NewTaskUploadFilesInBackground
            attachedId={newTaskId}
            filesToUpload={newTaskFiles}
            setFilesToUpload={setNewTaskFiles}
          />
        )}
      </Box>
    </>
  );
};
